/**
 * Collaboration tab — invite team members per saved project, leave comments
 * on budget items, record approvals. Audit trail shown at the bottom.
 *
 * Requires the calculation to be SAVED first (so it has a stable project_id).
 */
import React, { useEffect, useState } from 'react';
import ConstructionAPI from './api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { UserPlus, MessageSquare, CheckCircle2, History, Trash2, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import CostAgreementPanel from './CostAgreementPanel';

const ROLES = [
  { v: 'owner',    label: 'Owner' },
  { v: 'editor',   label: 'Editor' },
  { v: 'approver', label: 'Approver' },
  { v: 'viewer',   label: 'Viewer' },
];

export default function Collaboration({ calculation, savedProjectId, onSaveRequest }) {
  const projectId = savedProjectId;

  const [collab, setCollab] = useState({ members: [], comments: [], approvals: [], cost_agreements: [], audit: [] });
  const [loading, setLoading] = useState(false);

  // Invite
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');

  // Comment
  const [commentBody, setCommentBody] = useState('');
  const [commentTarget, setCommentTarget] = useState('budget');

  // Approval
  const [approvalDecision, setApprovalDecision] = useState('approved');
  const [approvalNote, setApprovalNote] = useState('');

  const refresh = async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      const res = await ConstructionAPI.getCollab(projectId);
      setCollab(res || { members: [], comments: [], approvals: [], cost_agreements: [], audit: [] });
    } catch (err) {
      // 404 means project not yet saved
      if (err.response?.status !== 404) console.error(err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [projectId]);

  if (!calculation) {
    return <Card><CardContent className="p-8 text-center text-sm text-slate-500">Run a calculation first.</CardContent></Card>;
  }
  if (!projectId) {
    return (
      <Card data-testid="collab-tab">
        <CardContent className="p-8 text-center">
          <MessageSquare className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-700 mb-1">Save the project to start collaborating.</p>
          <p className="text-xs text-slate-500 mb-4">Once saved, you can invite teammates by email, leave comments and record approvals.</p>
          <Button onClick={onSaveRequest} className="bg-orange-600 hover:bg-orange-700" data-testid="collab-save-btn">Save project &amp; enable collaboration</Button>
        </CardContent>
      </Card>
    );
  }

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return toast.error('Enter an email');
    try {
      await ConstructionAPI.inviteMember(projectId, inviteEmail.trim(), inviteRole);
      setInviteEmail('');
      toast.success(`Invited ${inviteEmail.trim()}`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invite failed');
    }
  };
  const handleRemove = async (email) => {
    try { await ConstructionAPI.removeMember(projectId, email); refresh(); }
    catch { toast.error('Remove failed'); }
  };
  const handleComment = async () => {
    if (!commentBody.trim()) return;
    try {
      await ConstructionAPI.addComment(projectId, commentBody.trim(), commentTarget, 'root');
      setCommentBody('');
      refresh();
    } catch { toast.error('Comment failed'); }
  };
  const handleResolve = async (id) => {
    try { await ConstructionAPI.resolveComment(projectId, id); refresh(); }
    catch { toast.error('Resolve failed'); }
  };
  const handleApproval = async () => {
    try {
      await ConstructionAPI.addApproval(projectId, approvalDecision, approvalNote);
      setApprovalNote('');
      toast.success('Decision recorded');
      refresh();
    } catch { toast.error('Approval failed'); }
  };

  return (
    <div className="space-y-4" data-testid="collab-tab">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Members */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base inline-flex items-center gap-1.5"><UserPlus className="h-4 w-4" />Team members</CardTitle>
            <p className="text-xs text-slate-500">Invite teammates by email. They&apos;ll see this project once they sign up with that email.</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <Input
                placeholder="email@company.com"
                type="email"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                className="h-9"
                data-testid="invite-email-input"
              />
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger className="h-9 w-32" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map(r => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
              <Button onClick={handleInvite} className="h-9" data-testid="invite-submit-btn"><UserPlus className="h-3.5 w-3.5 mr-1" />Invite</Button>
            </div>
            <div className="space-y-1.5">
              {(collab.members || []).map(m => (
                <div key={m.email} className="flex items-center justify-between gap-2 px-3 py-2 bg-slate-50 rounded-md text-sm" data-testid={`member-${m.email}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-slate-800 truncate">{m.email}</div>
                    <div className="text-[11px] text-slate-500">{m.status} · invited {m.invited_at ? new Date(m.invited_at).toLocaleDateString() : ''}</div>
                  </div>
                  <Badge variant="outline" className="capitalize text-xs">{m.role}</Badge>
                  <Button size="sm" variant="ghost" onClick={() => handleRemove(m.email)}><Trash2 className="h-3.5 w-3.5 text-red-600" /></Button>
                </div>
              ))}
              {(collab.members || []).length === 0 && <p className="text-xs text-slate-400 text-center py-2">No members yet.</p>}
            </div>
          </CardContent>
        </Card>

        {/* Approvals */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-600" />Approvals</CardTitle>
            <p className="text-xs text-slate-500">Record sign-offs from PMs and approvers.</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-col gap-2">
              <Select value={approvalDecision} onValueChange={setApprovalDecision}>
                <SelectTrigger className="h-9" data-testid="approval-decision"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="approved">Approve</SelectItem>
                  <SelectItem value="changes_requested">Request changes</SelectItem>
                  <SelectItem value="rejected">Reject</SelectItem>
                </SelectContent>
              </Select>
              <Textarea rows={2} placeholder="Add a note (optional)…" value={approvalNote} onChange={e => setApprovalNote(e.target.value)} className="text-sm" data-testid="approval-note" />
              <Button onClick={handleApproval} className="h-9" data-testid="approval-submit-btn">Record decision</Button>
            </div>
            <div className="space-y-1.5">
              {(collab.approvals || []).slice().reverse().map(a => (
                <div key={a.id} className="px-3 py-2 bg-slate-50 rounded-md text-sm">
                  <div className="flex items-center gap-2">
                    <ApprovalBadge decision={a.decision} />
                    <span className="text-xs text-slate-500">{new Date(a.created_at).toLocaleString()}</span>
                  </div>
                  {a.note && <div className="text-xs text-slate-700 mt-1">{a.note}</div>}
                  <div className="text-[10px] text-slate-400 mt-0.5">by {a.author_email}</div>
                </div>
              ))}
              {(collab.approvals || []).length === 0 && <p className="text-xs text-slate-400 text-center py-2">No decisions yet.</p>}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Comments */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base inline-flex items-center gap-1.5"><MessageSquare className="h-4 w-4" />Comments</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Select value={commentTarget} onValueChange={setCommentTarget}>
              <SelectTrigger className="h-9 w-36" data-testid="comment-target-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="budget">Budget</SelectItem>
                <SelectItem value="phase">Phase</SelectItem>
                <SelectItem value="risk">Risk</SelectItem>
                <SelectItem value="change">Change</SelectItem>
                <SelectItem value="funding">Funding</SelectItem>
              </SelectContent>
            </Select>
            <Input
              placeholder="Write a comment…"
              value={commentBody}
              onChange={e => setCommentBody(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleComment()}
              className="h-9"
              data-testid="comment-body-input"
            />
            <Button onClick={handleComment} className="h-9" data-testid="comment-submit-btn"><Send className="h-3.5 w-3.5 mr-1" />Post</Button>
          </div>
          <div className="space-y-1.5">
            {(collab.comments || []).slice().reverse().map(c => (
              <div key={c.id} className={`px-3 py-2 rounded-md text-sm ${c.status === 'resolved' ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50'}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px] capitalize">{c.target_type}</Badge>
                    <span className="text-xs text-slate-500">{new Date(c.created_at).toLocaleString()}</span>
                  </div>
                  {c.status !== 'resolved' && (
                    <Button size="sm" variant="ghost" onClick={() => handleResolve(c.id)}>Resolve</Button>
                  )}
                  {c.status === 'resolved' && <Badge className="bg-emerald-600 text-white text-[10px]">Resolved</Badge>}
                </div>
                <div className="text-sm text-slate-800 mt-1">{c.body}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">by {c.author_email}</div>
              </div>
            ))}
            {(collab.comments || []).length === 0 && <p className="text-xs text-slate-400 text-center py-2">No comments yet.</p>}
          </div>
        </CardContent>
      </Card>

      {/* Cost agreement (per-line voting) */}
      <CostAgreementPanel
        calculation={calculation}
        savedProjectId={projectId}
        members={collab.members}
        agreements={collab.cost_agreements}
        currentUser={(typeof window !== 'undefined') ? (() => { try { return JSON.parse(localStorage.getItem('user_profile') || '{}').uid; } catch { return ''; } })() : ''}
        onChange={refresh}
      />

      {/* Audit */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base inline-flex items-center gap-1.5"><History className="h-4 w-4" />Audit trail</CardTitle>
          <p className="text-xs text-slate-500">Last 50 events on this project.</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-1 max-h-72 overflow-y-auto" data-testid="audit-list">
            {(collab.audit || []).slice().reverse().map((e, i) => (
              <div key={i} className="text-xs text-slate-700 flex gap-2 items-start py-1 border-b border-slate-100 last:border-0">
                <span className="text-slate-500 font-mono shrink-0">{new Date(e.ts).toLocaleString()}</span>
                <Badge variant="outline" className="text-[10px] shrink-0">{e.event}</Badge>
                <span className="text-slate-600 truncate">by {e.actor}</span>
              </div>
            ))}
            {(collab.audit || []).length === 0 && <p className="text-xs text-slate-400 text-center py-2">No events yet.</p>}
          </div>
        </CardContent>
      </Card>
      {loading && <div className="text-xs text-slate-500"><Loader2 className="inline h-3 w-3 mr-1 animate-spin" />Loading…</div>}
    </div>
  );
}

function ApprovalBadge({ decision }) {
  if (decision === 'approved') return <Badge className="bg-emerald-600 text-white text-xs">Approved</Badge>;
  if (decision === 'rejected') return <Badge className="bg-red-600 text-white text-xs">Rejected</Badge>;
  return <Badge className="bg-amber-500 text-white text-xs">Changes requested</Badge>;
}
